/**
 * tpinApi — the controller for the TPIN-first endpoint family (ADR-005 v1.6).
 * Stores call THIS; route strings and wire details stay in the api layer.
 *
 * Contract rules implemented literally (TL packet §3.2):
 *  - an advanced cursor answered 400 is the published past-end terminal
 *    (§15), surfaced as the typed outcome `past-end`, never as a generic error;
 *  - `Cursor-Version` is checked on every paged success: directory `2`,
 *    branches `1` (summary carries none); a mismatch is a named ApiError;
 *  - a 503 `PartialResult` on /summary is the typed outcome `partial` and
 *    carries no totals (S6).
 */

import { requestWire, type WireResponse } from './apiClient'
import { mapBranchPage, mapDirectoryPage, mapTpinSummary } from './tpinMappers'
import { ApiError } from '@domain/types'
import type { ResolvedPeriod, TpinBranchPage, TpinDirectoryPage, TpinPeriodSummary } from '@domain/tpin'

const DIRECTORY_CURSOR_VERSION = '2'
const BRANCH_CURSOR_VERSION = '1'
const PARTIAL_RESULT = 'PartialResult'

function withQuery(path: string, params: Readonly<Record<string, string | number | undefined>>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value))
  }
  const query = search.toString()
  return query === '' ? path : `${path}?${query}`
}

/** The one place the TPIN-family route strings exist. */
export const tpinRoutes = {
  directory: (q?: string, cursor?: string, limit?: number): string =>
    withQuery('/api/agg/tpins', { q, cursor, limit }),
  summary: (tin: string, period: ResolvedPeriod): string =>
    withQuery(`/api/agg/tpins/${encodeURIComponent(tin)}/summary`, { from: period.from, to: period.to }),
  branches: (tin: string, period: ResolvedPeriod, cursor?: string, limit?: number): string =>
    withQuery(`/api/agg/tpins/${encodeURIComponent(tin)}/branches`, {
      from: period.from,
      to: period.to,
      cursor,
      limit,
    }),
} as const

/** True when an advance-with-cursor request was answered by the §15 past-end 400. */
export function isPastEnd(error: unknown, cursor: string | undefined): boolean {
  return cursor !== undefined && error instanceof ApiError && error.status === 400 && error.code === 'BadRequest'
}

/** True when the failure is the 503 `PartialResult` envelope. */
export function isPartialResult(error: unknown): boolean {
  return error instanceof ApiError && error.code === PARTIAL_RESULT
}

function assertCursorVersion(wire: WireResponse, expected: string): void {
  if (wire.cursorVersion !== expected) {
    throw new ApiError(
      502,
      'CURSOR_VERSION_MISMATCH',
      `Expected Cursor-Version ${expected} but the server sent ${wire.cursorVersion ?? 'none'}.`,
    )
  }
}

export type DirectoryOutcome =
  | { readonly kind: 'page'; readonly page: TpinDirectoryPage }
  | { readonly kind: 'past-end' }

export type SummaryOutcome =
  | { readonly kind: 'complete'; readonly summary: TpinPeriodSummary }
  | { readonly kind: 'partial'; readonly tin: string }

export type BranchPageOutcome =
  | { readonly kind: 'page'; readonly page: TpinBranchPage }
  | { readonly kind: 'past-end' }

export interface DirectoryQuery {
  readonly q?: string
  readonly limit?: number
}

/**
 * One directory page. An empty `q` is omitted from the request (empty input is
 * the server-paged browse, never a `q=` filter).
 */
async function getDirectoryPage(
  query: DirectoryQuery & { readonly cursor?: string },
  signal?: AbortSignal,
): Promise<TpinDirectoryPage> {
  const wire = await requestWire(
    tpinRoutes.directory(query.q === '' ? undefined : query.q, query.cursor, query.limit),
    signal,
  )
  assertCursorVersion(wire, DIRECTORY_CURSOR_VERSION)
  return mapDirectoryPage(wire.body)
}

/** The first page of a directory read — it has no cursor, so it cannot be "past the end". */
export function fetchDirectoryFirstPage(query: DirectoryQuery, signal?: AbortSignal): Promise<TpinDirectoryPage> {
  return getDirectoryPage(query, signal)
}

/**
 * Advance with a cursor. A 400 on an advanced cursor is the published §15
 * past-end terminal, returned as the typed outcome `past-end`.
 */
export async function fetchDirectoryAdvance(
  cursor: string,
  query: DirectoryQuery,
  signal?: AbortSignal,
): Promise<DirectoryOutcome> {
  try {
    return { kind: 'page', page: await getDirectoryPage({ ...query, cursor }, signal) }
  } catch (error) {
    if (isPastEnd(error, cursor)) return { kind: 'past-end' }
    throw error
  }
}

/** Coverage-qualified summary for one TPIN and period. */
export async function fetchTpinSummary(
  tin: string,
  period: ResolvedPeriod,
  signal?: AbortSignal,
): Promise<SummaryOutcome> {
  let wire: WireResponse
  try {
    wire = await requestWire(tpinRoutes.summary(tin, period), signal)
  } catch (error) {
    if (isPartialResult(error)) return { kind: 'partial', tin }
    throw error
  }
  return { kind: 'complete', summary: mapTpinSummary(wire.body) }
}

/** The published branch-page cap (ADR-005 §4); every S-DASH / S-BRANCH read asks for it. */
export const BRANCH_PAGE_LIMIT = 50

async function getBranchPage(
  tin: string,
  period: ResolvedPeriod,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<TpinBranchPage> {
  const wire = await requestWire(tpinRoutes.branches(tin, period, cursor, BRANCH_PAGE_LIMIT), signal)
  assertCursorVersion(wire, BRANCH_CURSOR_VERSION)
  return mapBranchPage(wire.body)
}

/** The first branch page (cursor is bound to tin/from/to); it has no cursor, so it cannot be "past the end". */
export function fetchBranchFirstPage(tin: string, period: ResolvedPeriod, signal?: AbortSignal): Promise<TpinBranchPage> {
  return getBranchPage(tin, period, undefined, signal)
}

/** Advance with a cursor; a 400 on an advanced cursor is the published §15 past-end terminal. */
export async function fetchBranchAdvance(
  tin: string,
  period: ResolvedPeriod,
  cursor: string,
  signal?: AbortSignal,
): Promise<BranchPageOutcome> {
  try {
    return { kind: 'page', page: await getBranchPage(tin, period, cursor, signal) }
  } catch (error) {
    if (isPastEnd(error, cursor)) return { kind: 'past-end' }
    throw error
  }
}
